import { useTranslation } from 'react-i18next';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import NumberInput from '../../ui/number-input';
import SectionHeading from './SectionHeading';
import { FormField, FormItem, FormControl, FormMessage } from '../../ui/form';
import { MachineFormValues } from '../types';

export default function SpatialGradientSection() {
    const { t } = useTranslation();
    const form = useFormContext<MachineFormValues>();
    const spatialGradientFields = useFieldArray({
        control: form.control,
        name: 'SpatialGradient',
    });

    return (
        <div className="space-y-3">
            <SectionHeading
                title={t(
                    'roomOverview.mriMachineForm.form.spatialGradient.label'
                )}
                description={t(
                    'roomOverview.mriMachineForm.form.spatialGradient.description'
                )}
            />
            {spatialGradientFields.fields.length === 0 ? (
                <p className="text-sm text-gray-400 py-4 text-center">
                    {t(
                        'roomOverview.mriMachineForm.form.spatialGradient.emptyState'
                    )}
                </p>
            ) : (
                <>
                    <div className="grid grid-cols-[2rem_1fr_1fr_2.5rem] gap-3 text-xs font-medium">
                        <span>#</span>
                        <span>
                            {t(
                                'roomOverview.mriMachineForm.form.spatialGradient.diameter',
                                { index: '' }
                            )}
                        </span>
                        <span>
                            {t(
                                'roomOverview.mriMachineForm.form.spatialGradient.gradient'
                            )}
                        </span>
                        <span />
                    </div>

                    {spatialGradientFields.fields.map((field, index) => (
                        <div
                            key={field.id}
                            className="grid grid-cols-[2rem_1fr_1fr_2.5rem] gap-3 items-start"
                        >
                            <span className="flex h-10 items-center text-xs tabular-nums">
                                {index + 1}
                            </span>
                            <FormField
                                control={form.control}
                                name={`SpatialGradient.${index}.diameter`}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Input
                                                type="text"
                                                placeholder={t(
                                                    'roomOverview.mriMachineForm.form.spatialGradient.diameterPlaceholder'
                                                )}
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name={`SpatialGradient.${index}.value`}
                                rules={{
                                    required: 'Spatial gradient is required',
                                }}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <NumberInput {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="mt-1 h-8 w-8 hover:text-destructive"
                                onClick={() =>
                                    spatialGradientFields.remove(index)
                                }
                                aria-label={t(
                                    'roomOverview.mriMachineForm.buttons.delete'
                                )}
                            >
                                <Trash2
                                    className="h-4 w-4"
                                    aria-hidden="true"
                                />
                            </Button>
                        </div>
                    ))}
                </>
            )}

            <div className="grid grid-cols-[2rem_1fr_1fr_2.5rem] gap-3">
                <div className="col-start-4 flex justify-end">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                            spatialGradientFields.append({
                                id: uuidv4(),
                                diameter: '',
                                value: 0,
                            })
                        }
                    >
                        <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
                        {t(
                            'roomOverview.mriMachineForm.form.spatialGradient.addButton'
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}
